import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Heading,
  Icon,
  LinkButton,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  useToast,
} from '@eluve/components';
import { timestampToHumanReadable } from '@eluve/date-utils';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { pluralize } from '@eluve/utils';

import { DupeGroupCard } from '../components/DupeGroupCard';
import { FlowStepper } from '../components/FlowStepper';
import { RowCard } from '../components/RowCard';
import { useImportJob } from '../hooks/useImportJob';
import { useImportRowMutations } from '../hooks/useImportRowMutations';
import { PATIENT_IMPORT_ROUTE, errMsg, fetchAllImportRows } from '../utils';

import { PatientImportFullFileView } from './PatientImportFullFileView';
import {
  type Counts,
  GroupSectionCard,
  type RowWithIssues,
  SECTION_ORDER,
  SectionLabel,
  SummaryStrip,
  bucketCategoriesBySeverity,
  groupRows,
  toRowWithIssues,
} from './review-grouping';

export const PatientImportReviewScreen: React.FC<{
  jobId: string;
  tenantId: string;
}> = ({ jobId, tenantId }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const job = useImportJob(tenantId, jobId);
  const currentUserId = useUserIdFromSession();
  const [revision, setRevision] = useState(0);
  const bumpRevision = useCallback(() => setRevision((r) => r + 1), []);
  const { updateField, resolveRow, unresolveRow, resolveBulk } =
    useImportRowMutations(tenantId, jobId, bumpRevision);

  const [counts, setCounts] = useState<Counts | null>(null);
  const [rows, setRows] = useState<RowWithIssues[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const [importRes, rowsResult] = await Promise.all([
          apiClient.patientImport.getImport({ params: { tenantId, jobId } }),
          fetchAllImportRows(apiClient, tenantId, jobId),
        ]);
        if (cancelled) return;
        if (importRes.status !== 200) {
          throw new Error(`Failed to load import (${importRes.status})`);
        }

        const next = rowsResult.rows.map((r) =>
          toRowWithIssues(r, rowsResult.issuesByRowId[r.id]),
        );
        if (cancelled) return;
        setCounts(importRes.body.counts);
        setRows(next);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        toast.error(`Failed to load review: ${errMsg(err)}`);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId, jobId, toast, job.updatedAt, revision]);

  const groups = useMemo(() => groupRows(rows), [rows]);
  const bucketed = useMemo(() => bucketCategoriesBySeverity(groups), [groups]);

  const isMultiAdmin = job.createdBy !== currentUserId;
  const blockingDisabled = !counts || counts.blocking > 0;
  const readyCount = counts?.ready ?? 0;
  const uploadedAgo = useMemo(
    () => timestampToHumanReadable(Date.parse(job.createdAt)),
    [job.createdAt],
  );

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack direction="horizontal" justify="between" align="start" gap={4}>
        <Stack gap={1}>
          <Heading size="3xl" weight="semibold">
            Review import
          </Heading>
          <Stack direction="horizontal" gap={2} align="center">
            <Icon name="FileText" size="xs" />
            <Text inline color="tertiary" size="sm" className="font-mono">
              {job.filename}
            </Text>
            {job.totalRows !== null ? (
              <Text inline color="tertiary" size="sm">
                · {job.totalRows} data rows
              </Text>
            ) : null}
            {uploadedAgo ? (
              <Text inline color="tertiary" size="sm">
                · uploaded {uploadedAgo}
              </Text>
            ) : null}
          </Stack>
        </Stack>
        <Stack direction="horizontal" gap={2} fullWidth={false}>
          <Link to={`${PATIENT_IMPORT_ROUTE}/new`}>
            <Button variant="outline">
              <Icon name="RefreshCw" size="auto" />
              Reupload file
            </Button>
          </Link>
          <LinkButton
            to={`${PATIENT_IMPORT_ROUTE}/${jobId}/finalize`}
            disabled={blockingDisabled}
          >
            {blockingDisabled && counts
              ? `Import · fix ${pluralize(counts.blocking, 'blocking error')} first`
              : `Continue to finalize · ${readyCount} patients`}
            <Icon name="ArrowRight" size="auto" />
          </LinkButton>
        </Stack>
      </Stack>

      <FlowStepper current="review" />

      {counts ? (
        <SummaryStrip counts={counts} />
      ) : (
        <Text color="tertiary">Loading summary…</Text>
      )}

      {isMultiAdmin ? (
        <Alert variant="warning">
          <Icon name="TriangleAlert" />
          <AlertTitle>Started by another admin</AlertTitle>
          <AlertDescription>
            This import was started by a different admin. Coordinate before
            resolving rows or finalizing.
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="grouped">
        <TabsList>
          <TabsTrigger value="grouped">Grouped by error</TabsTrigger>
          <TabsTrigger value="full">Full file</TabsTrigger>
        </TabsList>

        <TabsContent value="grouped">
          {!loaded ? (
            <Text color="tertiary">Loading rows…</Text>
          ) : rows.length === 0 ? (
            <Text color="tertiary">No rows.</Text>
          ) : SECTION_ORDER.every((s) => bucketed[s].length === 0) ? (
            <div className="flex items-center justify-center rounded-lg border py-12">
              <Text color="tertiary">No errors in this file</Text>
            </div>
          ) : (
            <Stack gap={4} align="stretch">
              {SECTION_ORDER.map((severity) => {
                const entries = bucketed[severity];
                if (entries.length === 0) return null;
                const sectionCount = entries.reduce(
                  (sum, e) => sum + e.rows.length,
                  0,
                );
                return (
                  <React.Fragment key={severity}>
                    <SectionLabel severity={severity} count={sectionCount} />
                    {entries.map((entry) => {
                      const onSkipAll = () =>
                        resolveBulk(
                          entry.rows
                            .filter((r) => r.resolution === null)
                            .map((r) => r.id),
                          'SKIPPED',
                        );
                      return (
                        <GroupSectionCard
                          key={entry.category}
                          category={entry.category}
                          rowCount={entry.rows.length}
                          unresolved={entry.unresolved}
                          onSkipAll={onSkipAll}
                          defaultOpen={severity !== 'info'}
                        >
                          {entry.dupeGroups.map((group) => (
                            <DupeGroupCard
                              key={group.map((r) => r.id).join('-')}
                              rows={group}
                              tenantId={tenantId}
                              jobId={jobId}
                              onMutationSuccess={bumpRevision}
                            />
                          ))}
                          {entry.singletons.map((row) => (
                            <RowCard
                              key={row.id}
                              row={row}
                              issues={row.issues}
                              tenantId={tenantId}
                              jobId={jobId}
                              onMutationSuccess={bumpRevision}
                            />
                          ))}
                        </GroupSectionCard>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Stack>
          )}
        </TabsContent>

        <TabsContent value="full">
          {counts ? (
            <PatientImportFullFileView
              rows={rows}
              counts={counts}
              updateField={updateField}
              resolveRow={resolveRow}
              unresolveRow={unresolveRow}
            />
          ) : null}
        </TabsContent>
      </Tabs>
    </Stack>
  );
};
